import { updateProfile } from '../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import {
  sendOneTimePassword,
  submitOnBuoyCrmRecord as submitOnBuoyCrmRecordApi,
  validateOneTimePassword,
} from '../util/api';
import { StatusCode } from '../util/enums';
import axios from 'axios';

const disposableDebounceBaseUrl = process.env.REACT_APP_DISPOSABLE_DEBOUNCE_URL;
const geoLocationUrl = process.env.REACT_APP_GEOLOCATION_DB_URL;

// Action Types
export const SEND_OTP_REQUEST = 'app/global/SEND_OTP_REQUEST';
export const SEND_OTP_SUCCESS = 'app/global/SEND_OTP_SUCCESS';
export const SEND_OTP_FAILURE = 'app/global/SEND_OTP_FAILURE';

export const VERIFY_OTP_REQUEST = 'app/global/VERIFY_OTP_REQUEST';
export const VERIFY_OTP_SUCCESS = 'app/global/VERIFY_OTP_SUCCESS';
export const VERIFY_OTP_FAILURE = 'app/global/VERIFY_OTP_FAILURE';

export const SUBMIT_ONBUOY_CRM_RECORD_REQUEST = 'app/global/SUBMIT_ONBUOY_CRM_RECORD_REQUEST';
export const SUBMIT_ONBUOY_CRM_RECORD_SUCCESS = 'app/global/SUBMIT_ONBUOY_CRM_RECORD_SUCCESS';
export const SUBMIT_ONBUOY_CRM_RECORD_FAILURE = 'app/global/SUBMIT_ONBUOY_CRM_RECORD_FAILURE';

export const CHECK_DISPOSABLE_REQUEST = 'app/global/CHECK_DISPOSABLE_REQUEST';
export const CHECK_DISPOSABLE_SUCCESS = 'app/global/CHECK_DISPOSABLE_SUCCESS';
export const CHECK_DISPOSABLE_FAILURE = 'app/global/CHECK_DISPOSABLE_FAILURE';

export const FETCH_GEOLOCATION_REQUEST = 'app/global/FETCH_GEOLOCATION_REQUEST';
export const FETCH_GEOLOCATION_SUCCESS = 'app/global/FETCH_GEOLOCATION_SUCCESS';
export const FETCH_GEOLOCATION_FAILURE = 'app/global/FETCH_GEOLOCATION_FAILURE';

// Action Creators
const sendOtpRequest = () => ({ type: SEND_OTP_REQUEST });
const sendOtpSuccess = data => ({ type: SEND_OTP_SUCCESS, payload: data });
const sendOtpFailure = error => ({ type: SEND_OTP_FAILURE, payload: error });

const verifyOtpRequest = () => ({ type: VERIFY_OTP_REQUEST });
const verifyOtpSuccess = data => ({ type: VERIFY_OTP_SUCCESS, payload: data });
const verifyOtpFailure = error => ({ type: VERIFY_OTP_FAILURE, payload: error });

const submitOnBuoyCrmRecordRequest = () => ({ type: SUBMIT_ONBUOY_CRM_RECORD_REQUEST });
const submitOnBuoyCrmRecordSuccess = data => ({
  type: SUBMIT_ONBUOY_CRM_RECORD_SUCCESS,
  payload: data,
});
const submitOnBuoyCrmRecordFailure = error => ({
  type: SUBMIT_ONBUOY_CRM_RECORD_FAILURE,
  payload: error,
});

const checkDisposableRequest = () => ({ type: CHECK_DISPOSABLE_REQUEST });
const checkDisposableSuccess = data => ({ type: CHECK_DISPOSABLE_SUCCESS, payload: data });
const checkDisposableFailure = error => ({ type: CHECK_DISPOSABLE_FAILURE, payload: error });

const fetchGeolocationRequest = () => ({ type: FETCH_GEOLOCATION_REQUEST });
const fetchGeolocationSuccess = data => ({ type: FETCH_GEOLOCATION_SUCCESS, payload: data });
const fetchGeolocationFailure = error => ({ type: FETCH_GEOLOCATION_FAILURE, payload: error });

// Thunk Actions
export const sendOtpToMobile = body => async dispatch => {
  dispatch(sendOtpRequest());
  try {
    const response = await sendOneTimePassword(body);
    dispatch(sendOtpSuccess(response.data));
    return response;
  } catch (error) {
    dispatch(sendOtpFailure(error.message));
    return error;
  }
};

export const verifyOneTimePassword = (body, param = {}) => async dispatch => {
  const { SignupForm = false, textNotificationsOptedIn = false } = param;
  dispatch(verifyOtpRequest());
  try {
    const response = await validateOneTimePassword(body);
    dispatch(verifyOtpSuccess(response.data));

    if (response?.statusCode === StatusCode.SUCCESS) {
      if (!SignupForm) {
        dispatch(
          updateProfile({
            protectedData: {
              isPhoneNumberVerified: true,
              phoneNumber: body?.phone,
              textNotificationsOptedIn,
            },
          })
        );
      }
    }
    return response;
  } catch (error) {
    dispatch(verifyOtpFailure(error.message));
    return error;
  }
};

export const submitOnBuoyCrmRecord = body => async dispatch => {
  dispatch(submitOnBuoyCrmRecordRequest());
  try {
    const response = await submitOnBuoyCrmRecordApi(body);
    dispatch(submitOnBuoyCrmRecordSuccess(response));
    return response;
  } catch (error) {
    dispatch(submitOnBuoyCrmRecordFailure(error?.data?.message || error.message));
    throw error;
  }
};

export const checkDisposableEmail = body => async dispatch => {
  dispatch(checkDisposableRequest());
  try {
    const email = body.email?.trim();
    if (!email) {
      throw new Error('No email provided');
    }
    const response = await axios.get(
      `${disposableDebounceBaseUrl}/?email=${encodeURIComponent(email)}`
    );
    dispatch(checkDisposableSuccess(response.data));
    return response.data;
  } catch (error) {
    dispatch(checkDisposableFailure(error.message));
    throw error;
  }
};

export const fetchGeolocation = () => async dispatch => {
  dispatch(fetchGeolocationRequest());
  try {
    const response = await axios.get(geoLocationUrl);
    dispatch(fetchGeolocationSuccess(response.data));
    return response.data;
  } catch (error) {
    dispatch(fetchGeolocationFailure(error.message));
    throw error;
  }
};

// Initial State
const initialState = {
  otpLoading: false,
  otpData: null,
  otpError: null,
  verifyLoading: false,
  verifyData: null,
  verifyError: null,
  addContactLoading: false,
  addContactData: null,
  addContactError: null,
  checkLoading: false,
  checkData: null,
  checkError: null,
  geolocationLoading: false,
  geolocationData: null,
  geolocationError: null,
};

// Reducer
const globalReducer = (state = initialState, action) => {
  switch (action.type) {
    case SEND_OTP_REQUEST:
      return { ...state, otpLoading: true, otpError: null };
    case SEND_OTP_SUCCESS:
      return { ...state, otpLoading: false, otpData: action.payload };
    case SEND_OTP_FAILURE:
      return { ...state, otpLoading: false, otpError: action.payload };
    case VERIFY_OTP_REQUEST:
      return { ...state, verifyLoading: true, verifyError: null };
    case VERIFY_OTP_SUCCESS:
      return { ...state, verifyLoading: false, verifyData: action.payload };
    case VERIFY_OTP_FAILURE:
      return { ...state, verifyLoading: false, verifyError: action.payload };
    case SUBMIT_ONBUOY_CRM_RECORD_REQUEST:
      return { ...state, addContactLoading: true, addContactError: null };
    case SUBMIT_ONBUOY_CRM_RECORD_SUCCESS:
      return { ...state, addContactLoading: false, addContactData: action.payload };
    case SUBMIT_ONBUOY_CRM_RECORD_FAILURE:
      return { ...state, addContactLoading: false, addContactError: action.payload };
    case CHECK_DISPOSABLE_REQUEST:
      return { ...state, checkLoading: true, checkError: null };
    case CHECK_DISPOSABLE_SUCCESS:
      return { ...state, checkLoading: false, checkData: action.payload };
    case CHECK_DISPOSABLE_FAILURE:
      return { ...state, checkLoading: false, checkError: action.payload };
    case FETCH_GEOLOCATION_REQUEST:
      return { ...state, geolocationLoading: true, geolocationError: null };
    case FETCH_GEOLOCATION_SUCCESS:
      return { ...state, geolocationLoading: false, geolocationData: action.payload };
    case FETCH_GEOLOCATION_FAILURE:
      return { ...state, geolocationLoading: false, geolocationError: action.payload };
    default:
      return state;
  }
};

export default globalReducer;
